import { useState, useCallback } from "react"
import { deleteProduct as deleteProductService } from "@/shared/services/products"
import { Product } from "@/shared/types/product"

interface UseDeleteProductResult {
  deleteDialogOpen: boolean
  productToDelete: Product | null
  deleting: boolean
  openDeleteDialog: (product: Product) => void
  closeDeleteDialog: () => void
  handleConfirmDelete: (userId: string, onSuccess: (deletedId: string) => void) => Promise<void>
}

const useDeleteProduct = (): UseDeleteProductResult => {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [productToDelete, setProductToDelete] = useState<Product | null>(null)
  const [deleting, setDeleting] = useState(false)

  const openDeleteDialog = useCallback((product: Product) => {
    setProductToDelete(product)
    setDeleteDialogOpen(true)
  }, [])

  const closeDeleteDialog = useCallback(() => {
    setDeleteDialogOpen(false)
    setProductToDelete(null)
  }, [])

  const handleConfirmDelete = useCallback(
    async (userId: string, onSuccess: (deletedId: string) => void) => {
      if (!productToDelete) return
      setDeleting(true)
      try {
        await deleteProductService(userId, productToDelete.id)
        onSuccess(productToDelete.id)
        closeDeleteDialog()
      } catch (error) {
        console.error("Error deleting product:", error)
        throw error
      } finally {
        setDeleting(false)
      }
    },
    [productToDelete, closeDeleteDialog]
  )

  return { deleteDialogOpen, productToDelete, deleting, openDeleteDialog, closeDeleteDialog, handleConfirmDelete }
}

export { useDeleteProduct }
